<script setup>
import { ref, reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import InboxHealthAPI from 'dashboard/api/inboxHealth';

import ButtonV4 from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import WhatsappReauthorize from '../channels/whatsapp/Reauthorize.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

// Same limits Meta enforces (and the server re-checks)
const LIMITS = { address: 256, description: 512, email: 128, website: 256 };
const PICTURE_TYPES = ['image/jpeg', 'image/png'];
const PICTURE_MAX_BYTES = 5 * 1024 * 1024;
const PICTURE_MIN_DIMENSION = 192;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const WEBSITE_REGEX = /^https?:\/\/\S+$/i;
const VERTICALS = [
  'ALCOHOL',
  'APPAREL',
  'AUTO',
  'BEAUTY',
  'EDU',
  'ENTERTAIN',
  'EVENT_PLAN',
  'FINANCE',
  'GOVT',
  'GROCERY',
  'HEALTH',
  'HOTEL',
  'NONPROFIT',
  'ONLINE_GAMBLING',
  'OTC_DRUGS',
  'OTHER',
  'PHYSICAL_GAMBLING',
  'PROF_SERVICES',
  'RESTAURANT',
  'RETAIL',
  'TRAVEL',
];

const isLoading = ref(false);
const isSaving = ref(false);
const loadErrorCode = ref(null);
const coexistence = ref(true);
const pictureUrl = ref('');
const pictureFile = ref(null);
const picturePreview = ref('');
const pictureError = ref('');
const serverErrors = ref({});
const form = reactive({
  vertical: '',
  description: '',
  address: '',
  email: '',
  websites: ['', ''],
});

// Editing is only possible once the profile loaded and Meta confirmed a Cloud-only number
const isLocked = computed(() => coexistence.value || !!loadErrorCode.value);

const verticalOptions = computed(() =>
  VERTICALS.map(value => ({
    value,
    label: t(`INBOX_MGMT.WHATSAPP_PROFILE.VERTICALS.${value}`),
  }))
);

const tooLong = max =>
  t('INBOX_MGMT.WHATSAPP_PROFILE.ERRORS.TOO_LONG', { max });

const SERVER_ERROR_MESSAGES = {
  too_long: field => tooLong(LIMITS[field]),
  invalid: field =>
    ({
      email: t('INBOX_MGMT.WHATSAPP_PROFILE.ERRORS.INVALID_EMAIL'),
      websites: t('INBOX_MGMT.WHATSAPP_PROFILE.ERRORS.INVALID_WEBSITE'),
      vertical: t('INBOX_MGMT.WHATSAPP_PROFILE.ERRORS.INVALID_VERTICAL'),
    })[field],
  too_many: () => t('INBOX_MGMT.WHATSAPP_PROFILE.ERRORS.TOO_MANY_WEBSITES'),
  invalid_type: () =>
    t('INBOX_MGMT.WHATSAPP_PROFILE.ERRORS.PICTURE_INVALID_TYPE'),
  too_large: () => t('INBOX_MGMT.WHATSAPP_PROFILE.ERRORS.PICTURE_TOO_LARGE'),
  too_small: () => t('INBOX_MGMT.WHATSAPP_PROFILE.ERRORS.PICTURE_TOO_SMALL'),
};

const serverError = field => {
  const code = serverErrors.value[field];
  return code ? SERVER_ERROR_MESSAGES[code]?.(field) || '' : '';
};

const fieldErrors = computed(() => {
  const errors = {
    description:
      form.description.length > LIMITS.description
        ? tooLong(LIMITS.description)
        : serverError('description'),
    address:
      form.address.length > LIMITS.address
        ? tooLong(LIMITS.address)
        : serverError('address'),
    email: serverError('email'),
    websites: form.websites.map(() => serverError('websites')),
  };
  if (form.email.length > LIMITS.email) errors.email = tooLong(LIMITS.email);
  else if (form.email && !EMAIL_REGEX.test(form.email))
    errors.email = t('INBOX_MGMT.WHATSAPP_PROFILE.ERRORS.INVALID_EMAIL');

  errors.websites = form.websites.map((url, index) => {
    if (!url) return errors.websites[index];
    if (url.length > LIMITS.website) return tooLong(LIMITS.website);
    return WEBSITE_REGEX.test(url)
      ? errors.websites[index]
      : t('INBOX_MGMT.WHATSAPP_PROFILE.ERRORS.INVALID_WEBSITE');
  });
  return errors;
});

const hasErrors = computed(
  () =>
    !!pictureError.value ||
    !!fieldErrors.value.description ||
    !!fieldErrors.value.address ||
    !!fieldErrors.value.email ||
    fieldErrors.value.websites.some(Boolean)
);

const lockTooltip = computed(() =>
  coexistence.value && !loadErrorCode.value
    ? t('INBOX_MGMT.WHATSAPP_PROFILE.COEXISTENCE_TOOLTIP')
    : ''
);

const isEmbeddedSignup = computed(
  () => props.inbox.provider_config?.source === 'embedded_signup'
);

const fillForm = ({ coexistence: isCoexistence, profile }) => {
  coexistence.value = isCoexistence;
  pictureUrl.value = profile.profile_picture_url || '';
  form.vertical = profile.vertical || '';
  form.description = profile.description || '';
  form.address = profile.address || '';
  form.email = profile.email || '';
  form.websites = [profile.websites?.[0] || '', profile.websites?.[1] || ''];
  pictureFile.value = null;
  picturePreview.value = '';
  pictureError.value = '';
  serverErrors.value = {};
};

let latestRequestId = 0;

const fetchProfile = async () => {
  latestRequestId += 1;
  const requestId = latestRequestId;
  isLoading.value = true;
  loadErrorCode.value = null;
  try {
    const { data } = await InboxHealthAPI.getWhatsappProfile(props.inbox.id);
    // Drop late responses after switching to another channel
    if (requestId === latestRequestId) fillForm(data);
  } catch (error) {
    if (requestId === latestRequestId)
      loadErrorCode.value = error.response?.data?.code || 'api_error';
  } finally {
    if (requestId === latestRequestId) isLoading.value = false;
  }
};

const readImageSize = file =>
  new Promise(resolve => {
    const image = new Image();
    const url = URL.createObjectURL(file);
    image.onload = () => resolve({ width: image.width, height: image.height });
    image.onerror = () => resolve(null);
    image.src = url;
  });

const pictureValidationError = async file => {
  if (!PICTURE_TYPES.includes(file.type)) return 'invalid_type';
  if (file.size > PICTURE_MAX_BYTES) return 'too_large';
  const size = await readImageSize(file);
  if (!size) return 'invalid_type';
  return size.width < PICTURE_MIN_DIMENSION ||
    size.height < PICTURE_MIN_DIMENSION
    ? 'too_small'
    : null;
};

const selectPicture = async file => {
  if (!file || isLocked.value) return;
  const errorCode = await pictureValidationError(file);
  if (errorCode) {
    pictureFile.value = null;
    picturePreview.value = '';
    pictureError.value = SERVER_ERROR_MESSAGES[errorCode]();
    return;
  }
  pictureError.value = '';
  pictureFile.value = file;
  picturePreview.value = URL.createObjectURL(file);
};

const onPictureInput = event => {
  selectPicture(event.target.files?.[0]);
  event.target.value = '';
};

const onPictureDrop = event => selectPicture(event.dataTransfer.files?.[0]);

const saveErrorMessage = code =>
  ({
    reauthorization_required: t(
      'INBOX_MGMT.WHATSAPP_PROFILE.ERRORS.REAUTHORIZATION'
    ),
    coexistence_locked: t(
      'INBOX_MGMT.WHATSAPP_PROFILE.ERRORS.COEXISTENCE_LOCKED'
    ),
  })[code] || t('INBOX_MGMT.WHATSAPP_PROFILE.ERRORS.SAVE');

const saveProfile = async () => {
  if (isLocked.value || hasErrors.value) return;
  isSaving.value = true;
  serverErrors.value = {};
  try {
    const { data } = await InboxHealthAPI.updateWhatsappProfile(
      props.inbox.id,
      {
        vertical: form.vertical,
        description: form.description,
        address: form.address,
        email: form.email,
        websites: form.websites.filter(Boolean),
        picture: pictureFile.value,
      }
    );
    fillForm(data);
    useAlert(t('INBOX_MGMT.WHATSAPP_PROFILE.SAVE_SUCCESS'));
  } catch (error) {
    // Typed values stay in the form so the user can fix them and retry
    const { code, errors } = error.response?.data || {};
    if (code === 'invalid_params') serverErrors.value = errors || {};
    if (code === 'coexistence_locked') coexistence.value = true;
    useAlert(saveErrorMessage(code));
  } finally {
    isSaving.value = false;
  }
};

const onLockedClick = () => {
  if (lockTooltip.value) useAlert(lockTooltip.value);
};

watch(() => props.inbox.id, fetchProfile, { immediate: true });
</script>

<template>
  <div class="mx-6 mb-4">
    <div
      class="px-5 py-5 space-y-6 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-2"
    >
      <div>
        <span class="text-heading-3 text-n-slate-12">
          {{ t('INBOX_MGMT.WHATSAPP_PROFILE.TITLE') }}
        </span>
        <p class="mt-1 text-body-main text-n-slate-11">
          {{ t('INBOX_MGMT.WHATSAPP_PROFILE.DESCRIPTION') }}
        </p>
      </div>

      <div v-if="isLoading" class="flex justify-center py-8">
        <Spinner />
      </div>

      <div
        v-else-if="loadErrorCode"
        class="flex flex-col gap-3 items-center py-6 text-center"
      >
        <WhatsappReauthorize
          v-if="
            loadErrorCode === 'reauthorization_required' && isEmbeddedSignup
          "
          :inbox="inbox"
          class="w-full"
        />
        <p class="text-body-main text-n-slate-11">
          {{
            loadErrorCode === 'reauthorization_required'
              ? t('INBOX_MGMT.WHATSAPP_PROFILE.ERRORS.REAUTHORIZATION')
              : t('INBOX_MGMT.WHATSAPP_PROFILE.ERRORS.FETCH')
          }}
        </p>
        <ButtonV4 sm faded slate @click="fetchProfile">
          {{ t('INBOX_MGMT.WHATSAPP_PROFILE.RETRY') }}
        </ButtonV4>
      </div>

      <form v-else class="flex flex-col gap-5" @submit.prevent="saveProfile">
        <div v-tooltip.top="lockTooltip" @click="onLockedClick">
          <fieldset
            :disabled="isLocked"
            class="flex flex-col gap-5"
            :class="{ 'pointer-events-none opacity-60': isLocked }"
          >
            <div class="flex flex-col gap-2">
              <span class="text-label-small text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.PICTURE.LABEL') }}
              </span>
              <label
                class="flex gap-4 items-center p-3 rounded-lg border border-dashed cursor-pointer border-n-weak bg-n-solid-1"
                @dragover.prevent
                @drop.prevent="onPictureDrop"
              >
                <img
                  v-if="picturePreview || pictureUrl"
                  :src="picturePreview || pictureUrl"
                  class="object-cover flex-shrink-0 w-16 h-16 rounded-full"
                />
                <span
                  v-else
                  class="flex flex-shrink-0 justify-center items-center w-16 h-16 rounded-full bg-n-alpha-2"
                >
                  <Icon icon="i-lucide-image" class="w-6 h-6 text-n-slate-9" />
                </span>
                <span class="flex flex-col gap-1">
                  <span class="text-label text-n-blue-11">
                    {{ t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.PICTURE.UPLOAD') }}
                  </span>
                  <span class="text-label-small text-n-slate-11">
                    {{ t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.PICTURE.HELP') }}
                  </span>
                </span>
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  class="hidden"
                  @change="onPictureInput"
                />
              </label>
              <span
                v-if="pictureError || serverError('picture')"
                class="text-label-small text-n-ruby-9"
              >
                {{ pictureError || serverError('picture') }}
              </span>
            </div>

            <div class="flex flex-col gap-1">
              <span class="text-label-small text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.VERTICAL.LABEL') }}
              </span>
              <Select
                v-model="form.vertical"
                :options="verticalOptions"
                :placeholder="
                  t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.VERTICAL.PLACEHOLDER')
                "
                :disabled="isLocked"
                :error="serverError('vertical')"
              />
            </div>

            <TextArea
              v-model="form.description"
              :label="t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.DESCRIPTION.LABEL')"
              :placeholder="
                t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.DESCRIPTION.PLACEHOLDER')
              "
              :max-length="LIMITS.description"
              show-character-count
              :disabled="isLocked"
              :message="fieldErrors.description"
              :message-type="fieldErrors.description ? 'error' : 'info'"
            />

            <Input
              v-model="form.address"
              :label="t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.ADDRESS.LABEL')"
              :placeholder="
                t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.ADDRESS.PLACEHOLDER')
              "
              :disabled="isLocked"
              :message="fieldErrors.address"
              :message-type="fieldErrors.address ? 'error' : 'info'"
            />

            <Input
              v-model="form.email"
              type="email"
              :label="t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.EMAIL.LABEL')"
              :placeholder="
                t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.EMAIL.PLACEHOLDER')
              "
              :disabled="isLocked"
              :message="fieldErrors.email"
              :message-type="fieldErrors.email ? 'error' : 'info'"
            />

            <Input
              v-for="(_, index) in form.websites"
              :key="index"
              v-model="form.websites[index]"
              type="url"
              :label="t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.WEBSITE.LABEL')"
              :placeholder="
                t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.WEBSITE.PLACEHOLDER')
              "
              :disabled="isLocked"
              :message="fieldErrors.websites[index]"
              :message-type="fieldErrors.websites[index] ? 'error' : 'info'"
            />
          </fieldset>
        </div>

        <div v-if="!isLocked">
          <ButtonV4
            type="submit"
            sm
            solid
            blue
            :is-loading="isSaving"
            :disabled="isSaving || hasErrors"
          >
            {{ t('INBOX_MGMT.WHATSAPP_PROFILE.SAVE') }}
          </ButtonV4>
        </div>
      </form>
    </div>
  </div>
</template>
